// ============================================================================
// Gmail — Webhook Adapter (Google Cloud Pub/Sub push delivery).
//
// Gmail doesn't POST webhooks directly; it publishes to a Pub/Sub topic that
// the operator configures, and Pub/Sub then POSTs to our ingress route. The
// shape of the POST body is:
//
//   {
//     "message": {
//       "data": "<base64 JSON payload>",
//       "messageId": "<pubsub message id>",
//       "publishTime": "2026-04-17T00:00:00Z"
//     },
//     "subscription": "projects/.../subscriptions/..."
//   }
//
// The base64 `data` decodes to:
//
//   { "emailAddress": "user@domain", "historyId": "123456" }
//
// Historyid is the CHECKPOINT — we then call `users.history.list?startHistoryId=`
// to resolve the actual message ids that changed since our last stored
// checkpoint. That resolution step is the adapter's job; the ingress pipeline
// just records the raw event.
//
// Auth model (why per-instance bearer token, not Google-issued JWT):
//   Pub/Sub push subscriptions CAN be configured with "authentication" —
//   Google then issues an OIDC JWT per request, signed by Google, carrying
//   the subscription's service account as the `sub`. Verifying those JWTs
//   requires:
//     1. Fetching Google's public JWKs (https://www.googleapis.com/oauth2/v3/certs)
//     2. Validating `aud` matches our webhook URL
//     3. Validating `iss` is accounts.google.com
//     4. Checking the signature
//   That's a lot of runtime surface for a service-to-service hop. We instead
//   use a SIMPLER but equally secure primitive: a per-instance bearer token
//   passed via the `Authorization` header (the operator configures the
//   Pub/Sub subscription with `authMethod=authentication-header-bearer` and
//   our per-instance secret as the bearer value; the secret lives in
//   `connector_instances.config.webhook_bearer_token`). We verify with a
//   timing-safe compare. If the operator prefers the Google OIDC path we
//   document a `?token=<per-instance-secret>` fallback on the URL — still
//   timing-safe, bound to one instance.
// ============================================================================

import {
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'
import { base64UrlDecodeToString } from './mime'
import { GmailSignatureError } from './types'

// ============================================================================
// Raw Pub/Sub payload shapes
// ============================================================================

interface PubSubEnvelope {
  message?: {
    data?: string
    messageId?: string
    publishTime?: string
    attributes?: Record<string, string>
  }
  subscription?: string
}

interface GmailHistoryNotification {
  emailAddress?: string
  /** Numeric as string (JSON wire shape from Gmail). */
  historyId?: string
}

// ============================================================================
// verifyWebhook
// ============================================================================

/**
 * Verify the Pub/Sub push request is from a caller that knows our per-instance
 * bearer token via `Authorization: Bearer <token>`.
 *
 * The expected token lives on the instance config
 * (`instance.config.webhook_bearer_token` — plaintext in the config JSON; it's
 * already per-instance and meaningless outside this route).
 *
 * Always uses timing-safe compare (`timingSafeEqualBytes`). Fails closed when
 * the config is missing so misconfigured instances don't accept unauthed
 * webhook deliveries.
 *
 * Round 1 C2 fix: the previous `?token=<secret>` query-param fallback + the
 * `x-kubdee-webhook-token` header it relied on were dead code — no ingress
 * route stamped the header. Operators who need query-based auth can stand up
 * a thin Pub/Sub push proxy that rewrites `?token=` → `Authorization`, or
 * use Google OIDC with a cross-verified service-account key.
 */
async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  const expected = pickExpectedToken(ctx)
  if (!expected) return false

  const candidate = pickProvidedToken(ctx)
  if (!candidate) return false

  const expectedBytes = new TextEncoder().encode(expected)
  const candidateBytes = new TextEncoder().encode(candidate)
  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

function pickExpectedToken(ctx: WebhookAdapterContext): string | null {
  const raw = ctx.instance.config
  if (!raw) return null
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>)
    const token = (parsed as { webhook_bearer_token?: unknown })?.webhook_bearer_token
    if (typeof token === 'string' && token.length > 0) return token
  } catch {
    return null
  }
  return null
}

function pickProvidedToken(ctx: WebhookAdapterContext): string | null {
  const authHeader = ctx.headers.get('authorization')
  if (authHeader && /^bearer\s+/i.test(authHeader)) {
    const bearer = authHeader.replace(/^bearer\s+/i, '').trim()
    if (bearer.length > 0) return bearer
  }
  return null
}

// ============================================================================
// parseWebhook
// ============================================================================

/**
 * Parse a Pub/Sub push envelope into a single `ParsedWebhookEvent`. The
 * returned event carries the Pub/Sub `messageId` as its `eventId` — that's
 * provider-assigned + unique, so retries (Pub/Sub will redeliver if we
 * return non-2xx) collapse on the same dedupe key.
 *
 * The payload carries `historyId` only — resolving which message ids changed
 * is done by the dispatcher / hook that subscribed to the adapter, via
 * `history.list`. Attempting to resolve here would couple webhook parsing
 * with an outbound API call, which the ingress contract forbids (parsing
 * must be sync + hermetic).
 */
async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  const text = new TextDecoder().decode(new Uint8Array(ctx.rawBody))
  let envelope: PubSubEnvelope
  try {
    envelope = JSON.parse(text) as PubSubEnvelope
  } catch {
    // Malformed envelope → surface as invalid_payload (the ingress pipeline
    // treats parser errors as 400).
    throw new SyntaxError('Pub/Sub envelope is not valid JSON')
  }

  const message = envelope?.message
  if (!message || typeof message.data !== 'string' || message.data.length === 0) {
    throw new SyntaxError('Pub/Sub envelope missing message.data')
  }
  const messageId = typeof message.messageId === 'string' ? message.messageId : ''
  if (!messageId) {
    throw new SyntaxError('Pub/Sub envelope missing message.messageId')
  }

  // Base64-decode the inner payload — Pub/Sub uses STANDARD base64 (not
  // base64url) for the `data` field. Tolerate either by passing through our
  // url-safe decoder (it accepts both after normalisation).
  let inner: GmailHistoryNotification
  try {
    const decoded = base64UrlDecodeToString(message.data)
    inner = JSON.parse(decoded) as GmailHistoryNotification
  } catch {
    throw new SyntaxError('Pub/Sub message.data is not valid base64 JSON')
  }
  const historyId = typeof inner?.historyId === 'string' ? inner.historyId : ''
  const emailAddress = typeof inner?.emailAddress === 'string' ? inner.emailAddress : ''
  if (!historyId) {
    throw new SyntaxError('Pub/Sub payload missing historyId')
  }

  const publishTime = typeof message.publishTime === 'string' ? message.publishTime : new Date().toISOString()

  // We emit the event as `email.history.changed` (NOT `email.received`) —
  // the actual `email.received` fan-out happens after the dispatcher runs
  // `history.list` against the instance's stored baseline historyId. Keeping
  // the webhook adapter hermetic preserves the parse / verify contract.
  //
  // `instanceId` + `companyId` are stamped by the consuming ingress service
  // (see modules/connectors/services/webhook-ingress.service.ts) — the
  // adapter has no visibility into platform ids. Empty strings match the
  // FB / Line / IG adapter convention.
  void ctx
  return [
    {
      eventId: messageId,
      eventType: 'email.history.changed',
      // Not one of the canonical `CapabilityMap['email']['events']` keys —
      // leave `capability` undefined so the ingress pipeline logs the
      // event but does not attempt to emit it on the typed bus. The
      // dispatcher reads directly from `connectors_webhook_events` to
      // drive `history.list`.
      payload: {
        event: 'history.changed',
        instanceId: '',
        companyId: '',
        emailAddress,
        historyId,
        publishTime,
      },
    },
  ]
}

// ============================================================================
// Public verifier — exposed so tests / tooling can verify a Pub/Sub envelope
// without hitting the full adapter context.
// ============================================================================

export function verifyGmailWebhookToken(
  provided: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  if (!provided || !expected) return false
  const expectedBytes = new TextEncoder().encode(expected)
  const candidateBytes = new TextEncoder().encode(provided)
  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

/**
 * Public parser — accept raw bytes and parse without verifying auth. Returns
 * the `{ emailAddress, historyId, publishTime, messageId }` tuple so callers
 * can resolve changed message ids via `history.list`.
 *
 * Throws `GmailSignatureError` on malformed / missing fields so callers can
 * distinguish envelope errors from Gmail API errors.
 */
export function parseGmailPubSubEnvelope(rawBody: ArrayBuffer | Uint8Array | string): {
  emailAddress: string
  historyId: string
  publishTime: string
  messageId: string
} {
  const text =
    typeof rawBody === 'string'
      ? rawBody
      : rawBody instanceof Uint8Array
        ? new TextDecoder().decode(rawBody)
        : new TextDecoder().decode(new Uint8Array(rawBody))
  let envelope: PubSubEnvelope
  try {
    envelope = JSON.parse(text) as PubSubEnvelope
  } catch {
    throw new GmailSignatureError('Pub/Sub envelope is not valid JSON')
  }
  const message = envelope?.message
  if (!message || typeof message.data !== 'string') {
    throw new GmailSignatureError('Pub/Sub envelope missing message.data')
  }
  const messageId = typeof message.messageId === 'string' ? message.messageId : ''
  if (!messageId) {
    throw new GmailSignatureError('Pub/Sub envelope missing message.messageId')
  }
  let inner: GmailHistoryNotification
  try {
    inner = JSON.parse(base64UrlDecodeToString(message.data)) as GmailHistoryNotification
  } catch {
    throw new GmailSignatureError('Pub/Sub message.data is not valid base64 JSON')
  }
  if (typeof inner?.historyId !== 'string' || inner.historyId.length === 0) {
    throw new GmailSignatureError('Pub/Sub payload missing historyId')
  }
  return {
    emailAddress: typeof inner.emailAddress === 'string' ? inner.emailAddress : '',
    historyId: inner.historyId,
    publishTime: typeof message.publishTime === 'string' ? message.publishTime : '',
    messageId,
  }
}

// ============================================================================
// Adapter export
// ============================================================================

export const gmailWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}
